import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Search, CheckCircle2, PauseCircle, Trash2, ExternalLink, RefreshCcw, Landmark, X } from "lucide-react";

const PLAN_STYLE = {
  active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  founders: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  trial: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  suspended: "bg-rose-500/20 text-rose-300 border-rose-500/40",
};
const PLAN_LABEL = {
  active: "ATTIVA",
  founders: "FOUNDERS",
  trial: "TRIAL",
  suspended: "SOSPESA",
};

export default function SuperAdminLeagues() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("all");
  const [bankModal, setBankModal] = useState(null); // {league_id, name, amount}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (plan !== "all") params.plan = plan;
      const { data } = await api.get("/superadmin/leagues", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, plan]);

  useEffect(() => { load(); }, [load]);

  async function setLeaguePlan(id, newPlan) {
    try {
      await api.post(`/superadmin/leagues/${id}/set-plan`, { plan: newPlan });
      toast.success(`Piano aggiornato a ${newPlan}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  async function deleteLeague(id, name) {
    if (!window.confirm(`Eliminare la lega "${name}" e TUTTI i suoi dati? Operazione irreversibile.`)) return;
    try {
      await api.delete(`/superadmin/leagues/${id}`);
      toast.success("Lega eliminata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function submitBankTransfer(payload) {
    try {
      const { data } = await api.post(
        `/superadmin/leagues/${bankModal.league_id}/mark-paid-bank-transfer`,
        payload,
      );
      if (data.already_active) {
        toast.info("La lega era già attiva. Nessuna modifica.");
      } else {
        toast.success(`Lega attivata via bonifico (${data.amount} €).`);
      }
      setBankModal(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="p-6 sm:p-8 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tight" data-testid="sa-leagues-title">Gestione Leghe</h1>
        <button
          onClick={load}
          data-testid="sa-leagues-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-700 hover:border-rose-500 hover:bg-rose-500/10 text-sm text-slate-300"
        >
          <RefreshCcw size={14}/> Ricarica
        </button>
      </div>

      <div className="flex flex-wrap gap-3 items-center bg-slate-900/60 border border-slate-800 rounded-xl p-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
          <input
            data-testid="sa-leagues-search"
            className="w-full bg-slate-950 border border-slate-700 rounded-md pl-9 pr-4 py-2 text-sm focus:border-rose-500 outline-none"
            placeholder="Cerca per nome lega…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          data-testid="sa-leagues-plan-filter"
          className="bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm focus:border-rose-500 outline-none"
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
        >
          <option value="all">Tutti i piani</option>
          <option value="active">Attive</option>
          <option value="trial">Trial</option>
          <option value="founders">Founders</option>
          <option value="suspended">Sospese</option>
        </select>
        <div className="text-xs text-slate-500 font-mono">{rows.length} leghe</div>
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/70 text-[10px] uppercase tracking-widest text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left">Lega</th>
                <th className="px-4 py-3 text-left">Admin</th>
                <th className="px-4 py-3 text-center">Piano</th>
                <th className="px-4 py-3 text-center">Fase</th>
                <th className="px-4 py-3 text-center">Squadre</th>
                <th className="px-4 py-3 text-center">Manager</th>
                <th className="px-4 py-3 text-left">Creata</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-slate-500">Caricamento…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-slate-500">Nessuna lega trovata</td></tr>
              ) : rows.map((lg) => (
                <tr key={lg.league_id} className="hover:bg-slate-800/30" data-testid={`sa-league-row-${lg.league_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-bold text-slate-100">{lg.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{lg.league_id} · code {lg.code}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-200">{lg.admin_name || "—"}</div>
                    <div className="text-xs text-slate-500">{lg.admin_email || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded border ${PLAN_STYLE[lg.plan] || "bg-slate-800 border-slate-700 text-slate-300"}`}>
                      {PLAN_LABEL[lg.plan] || lg.plan || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-mono text-slate-300">{lg.season_phase ?? "—"}</td>
                  <td className="px-4 py-3 text-center font-mono">{lg.team_count}</td>
                  <td className="px-4 py-3 text-center font-mono">{lg.manager_count}</td>
                  <td className="px-4 py-3 text-xs text-slate-400 font-mono">{(lg.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {lg.plan !== "active" && lg.plan !== "founders" && (
                      <button
                        data-testid={`sa-activate-${lg.league_id}`}
                        onClick={() => setLeaguePlan(lg.league_id, "active")}
                        title="Attiva (bypass pagamento)"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-600/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-xs"
                      >
                        <CheckCircle2 size={12}/> Attiva
                      </button>
                    )}
                    {lg.plan !== "active" && lg.plan !== "founders" && (
                      <button
                        data-testid={`sa-mark-bank-${lg.league_id}`}
                        onClick={() => setBankModal({ league_id: lg.league_id, name: lg.name, amount: 69 })}
                        title="Segna come pagata via bonifico bancario"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-sky-600/40 bg-sky-500/10 hover:bg-sky-500/20 text-sky-300 text-xs"
                      >
                        <Landmark size={12}/> Bonifico
                      </button>
                    )}
                    {lg.plan !== "suspended" && (
                      <button
                        data-testid={`sa-suspend-${lg.league_id}`}
                        onClick={() => setLeaguePlan(lg.league_id, "suspended")}
                        title="Sospendi"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-600/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-xs"
                      >
                        <PauseCircle size={12}/> Sospendi
                      </button>
                    )}
                    <a
                      href={`/lega/${lg.league_id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`sa-enter-league-${lg.league_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-sky-600/40 bg-sky-500/10 hover:bg-sky-500/20 text-sky-300 text-xs font-bold"
                      title="Entra nella lega come SuperAdmin (bypass permessi)"
                    >
                      <ExternalLink size={12}/> Entra
                    </a>
                    <button
                      data-testid={`sa-delete-${lg.league_id}`}
                      onClick={() => deleteLeague(lg.league_id, lg.name)}
                      title="Elimina lega"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-600/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 text-xs"
                    >
                      <Trash2 size={12}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {bankModal && (
        <BankTransferModal
          league={bankModal}
          onClose={() => setBankModal(null)}
          onSubmit={submitBankTransfer}
        />
      )}
    </div>
  );
}

function BankTransferModal({ league, onClose, onSubmit }) {
  const [amount, setAmount] = useState(league.amount ?? 69);
  const [reference, setReference] = useState("");
  const [payerEmail, setPayerEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        amount: Number(amount) || null,
        reference: reference.trim() || null,
        payer_email: payerEmail.trim() || null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="sa-bank-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handle}
        className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2">
          <Landmark size={16} className="text-sky-400"/>
          <div className="font-bold text-slate-100">Segna come pagata via bonifico</div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-slate-500 hover:text-slate-200"
            data-testid="sa-bank-modal-close"
          >
            <X size={16}/>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 text-sm">
          <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-3 text-xs text-slate-300 leading-relaxed">
            Attiverai <b className="text-slate-100">{league.name}</b> come lega pagata
            {" "}(<span className="font-mono text-slate-400">{league.league_id}</span>).
            Verrà creato un record contabile <b>bank_transfer</b> nella tabella pagamenti
            e la lega passerà a <span className="text-emerald-300 font-bold">plan=active</span>.
          </div>

          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Importo ricevuto (EUR)</div>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              data-testid="sa-bank-modal-amount"
              className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm focus:border-sky-500 outline-none font-mono"
            />
          </label>

          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Email pagatore (opzionale)</div>
            <input
              type="email"
              value={payerEmail}
              onChange={(e) => setPayerEmail(e.target.value)}
              placeholder="mario.rossi@example.com"
              data-testid="sa-bank-modal-email"
              className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm focus:border-sky-500 outline-none"
            />
          </label>

          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Riferimento bonifico (opzionale)</div>
            <textarea
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              rows={2}
              placeholder="Es: CRO 12345 · Ricevuto 08/08/26 conto Poste"
              data-testid="sa-bank-modal-reference"
              className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm resize-none focus:border-sky-500 outline-none"
            />
          </label>
        </div>

        <div className="px-5 py-4 border-t border-slate-800 flex items-center gap-2 bg-slate-950/40">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-slate-700 hover:bg-slate-800 text-slate-300 text-sm"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="sa-bank-modal-submit"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-sky-500 hover:bg-sky-400 text-slate-950 text-sm font-bold disabled:opacity-60"
          >
            <Landmark size={14}/> {submitting ? "Attivando…" : "Conferma e attiva"}
          </button>
        </div>
      </form>
    </div>
  );
}
